import { clearCurrentTenant, clearTokens, getAccessToken, getCurrentTenant, getRefreshToken, hasWebSessionCookie, refreshSession, setTokens } from '@/shared/infrastructure/repositories';
import { ApiError, TransientAuthError } from '@yawi/infrastructure';
import { User } from '@/shared/types';
import { safeStorage } from '@/shared/utils/safeStorage';
import { writeSettingsCache } from '@/shared/utils/settingsCache';
import type { ImpersonatingUser, MagicLinkVerifyResponse, OriginalAdmin, TenantSelection, TenantWithBranding } from '@yawi/types';
import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useToast } from './ToastContext';
import { useDI } from './DIContext';

// Evita resincronizar en ráfaga si varias requests fallan con 403 al mismo tiempo
// (ej. un dashboard que dispara varias llamadas en paralelo tras perder un permiso).
const FORBIDDEN_RESYNC_THROTTLE_MS = 5000;

// Debe coincidir con el default de DIContext: sin VITE_API_MODE se asume backend real.
const API_MODE = import.meta.env.VITE_API_MODE || 'api';
const IMPERSONATION_STORAGE_KEY = 'yawi_impersonation';
const IMPERSONATION_COOKIE_MAX_AGE_SECONDS = 7 * 24 * 60 * 60; // matches refresh token TTL

function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

function setCookie(name: string, value: string, maxAgeSeconds: number): void {
  // Lleva tokens del admin original: Secure para que nunca viaje por http en producción.
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${name}=${encodeURIComponent(value)}; max-age=${maxAgeSeconds}; path=/; SameSite=Lax${secure}`;
}

function removeCookie(name: string): void {
  document.cookie = `${name}=; max-age=0; path=/; SameSite=Lax`;
}

export interface ImpersonationState {
  originalAdmin: OriginalAdmin;
  impersonating: ImpersonatingUser;
}

interface ImpersonationSnapshot extends ImpersonationState {
  originalAccessToken: string;
  originalRefreshToken: string | null;
  originalUser: User;
}

export interface AuthContextType {
  currentUser: User | null;
  setCurrentUser: React.Dispatch<React.SetStateAction<User | null>>;
  isLoadingUser: boolean;
  /** Tenant scoping for FAMILIAR role (set on magic-link / tenant-select flows). */
  currentTenant: TenantWithBranding | null;
  /** Lanza TenantSelectionRequiredError si el correo resuelve a más de una cuenta en distintos tenants. */
  login: (email: string, pin: string, tenantId?: string) => Promise<void>;
  /**
   * Cambia de residencial. Cada tenant es una cuenta distinta con su propia
   * contraseña, así que esto es un login dirigido, no un cambio de contexto.
   */
  switchTenant: (tenant: TenantSelection, password: string) => Promise<void>;
  logout: () => void;
  loginWithMagicLink: (response: MagicLinkVerifyResponse) => Promise<void>;
  selectTenantForMagicLink: (userId: string, tenantId: string) => Promise<MagicLinkVerifyResponse>;
  updateCurrentUser: (userData: Partial<User>) => Promise<void>;
  /** Re-obtiene currentUser desde el backend (ej. tras setInitialPassword, para reflejar el nuevo passwordSetByUser). */
  refreshCurrentUser: () => Promise<void>;
  /** Admin previewing the app as another member of their tenant (null when not active). */
  impersonation: ImpersonationState | null;
  startImpersonation: (memberId: string) => Promise<void>;
  exitImpersonation: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { userRepo, settingsRepo, setOnUnauthorizedHandler, setOnForbiddenHandler } = useDI();
  const toast = useToast();
  const lastForbiddenResyncRef = useRef(0);

  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [isLoadingUser, setIsLoadingUser] = useState(true);
  const [currentTenant, setCurrentTenant] = useState<TenantWithBranding | null>(null);
  const [impersonation, setImpersonation] = useState<ImpersonationState | null>(null);

  // El tenant de la sesión lo persiste el apiClient (yawi_tenant) al loguear;
  // acá solo se sube al estado para que la UI pueda mostrarlo.
  const hydrateCurrentTenant = (): void => {
    const stored = getCurrentTenant();
    if (!stored) return;
    // El backend solo emite sesión para tenants activos y su respuesta de login
    // no incluye `status`; se completa para encajar con TenantWithBranding.
    setCurrentTenant({ status: 'ACTIVE', ...stored } as TenantWithBranding);
  };

  // Register the auth-layer's 401 handler into the DI layer so apiClient can call it.
  useEffect(() => {
    setOnUnauthorizedHandler(() => {
      setCurrentUser(null);
      safeStorage.removeItem('hommi_user');
      clearTokens();
    });
  }, [setOnUnauthorizedHandler]);

  // 403 a mitad de sesión: el JWT sigue siendo válido pero el backend negó el permiso.
  // Puede pasar si un admin le quitó/cambió el rol a este usuario mientras tenía la
  // pestaña abierta. En vez de esperar a que expire el token o forzar logout,
  // resincronizamos currentUser (con eso, AbilityProvider recalcula las habilidades
  // CASL en el siguiente render).
  //
  // El toast solo se muestra para mutaciones (POST/PUT/PATCH/DELETE) — algo que el
  // usuario disparó a propósito con un click, donde "no tienes permiso" es información
  // útil. Un GET de fondo (ej. la app cargando /settings al iniciar sesión) puede fallar
  // con 403 de forma esperada para ciertos roles sin que sea un evento que valga la pena
  // interrumpir al usuario — antes fallaba en silencio y así debe seguir.
  useEffect(() => {
    setOnForbiddenHandler((message, method) => {
      const now = Date.now();
      if (now - lastForbiddenResyncRef.current < FORBIDDEN_RESYNC_THROTTLE_MS) return;
      lastForbiddenResyncRef.current = now;

      if (method !== 'GET') {
        toast.error(
          message || 'No tienes permiso para realizar esta acción.',
          'Acceso denegado'
        );
      }

      userRepo.getCurrentUser()
        .then((user) => {
          setCurrentUser(user);
          safeStorage.setItem('hommi_user', JSON.stringify(user));
        })
        .catch(() => {
          // Si falla el resync (ej. problema de red), no insistimos — el próximo
          // 403 lo reintentará.
        });
    });
  }, [setOnForbiddenHandler, userRepo, toast]);

  // Session restoration on app boot
  useEffect(() => {
    const init = async () => {
      if (API_MODE === 'api') {
        // El refresh token ya no se persiste en localStorage (vive en cookie
        // HttpOnly) — la señal de "puede haber sesión" es la cookie legible
        // yawi_csrf. Si no está, no vale la pena intentar restaurar sesión.
        if (hasWebSessionCookie()) {
          const restoreSession = async (): Promise<void> => {
            // Pedimos el access token de forma proactiva antes de la primera
            // request de negocio. Evita el 401 "esperado" (sin token en
            // memoria tras el reload) que el flujo reactivo de HttpClient
            // igual resuelve solo, pero generando una request de más y ruido
            // en consola/Sentry en cada carga de página.
            await refreshSession();
            const user = await userRepo.getCurrentUser();
            setCurrentUser(user);
            safeStorage.setItem('hommi_user', JSON.stringify(user));
            hydrateCurrentTenant();
          };

          try {
            await restoreSession();
          } catch (err) {
            // Un 429 (rate limit — más probable con varias pestañas abiertas
            // a la vez, cada una restaurando sesión al bootear) o un backend
            // caído no significan que la sesión sea inválida, solo que no se
            // pudo confirmar en este intento. Reintentamos una vez tras una
            // breve espera en vez de desloguear a un usuario con sesión válida.
            const isTransient = err instanceof TransientAuthError
              || (err instanceof ApiError && err.statusCode === 429);

            if (!isTransient) {
              clearTokens();
            } else {
              await new Promise((resolve) => setTimeout(resolve, 1500));
              try {
                await restoreSession();
              } catch {
                // Sigue sin poder confirmar la sesión — no forzamos logout de
                // todas formas (los tokens siguen siendo válidos). El próximo
                // reload o la primera request de negocio (flujo reactivo de
                // HttpClient) lo reintentará.
              }
            }
          }
        }
      } else {
        const stored = safeStorage.getItem('hommi_user');
        if (stored) setCurrentUser(JSON.parse(stored));
      }

      // Resume the impersonation banner across a page refresh — tokens are
      // already active in storage via setTokens, only the display state needs restoring.
      const storedImpersonation = getCookie(IMPERSONATION_STORAGE_KEY);
      if (storedImpersonation) {
        try {
          const snapshot: ImpersonationSnapshot = JSON.parse(storedImpersonation);
          setImpersonation({ originalAdmin: snapshot.originalAdmin, impersonating: snapshot.impersonating });
        } catch {
          removeCookie(IMPERSONATION_STORAGE_KEY);
        }
      }

      setIsLoadingUser(false);
    };
    init();
  }, [userRepo]);

  // Se detecta acá en vez de en el próximo boot (como decía el diseño original)
  // porque no hay forma confiable de "recordar" entre reloads que el problema
  // fue justo esto: si el navegador bloquea todo el storage del sitio (caso
  // típico), también bloquea localStorage/sessionStorage, así que no queda
  // ningún lugar donde dejar una marca que sobreviva al reload. Comprobar
  // inmediatamente después del login si la cookie yawi_csrf llegó es la única
  // señal disponible que no depende de storage.
  const warnIfSessionWontPersist = () => {
    if (API_MODE === 'api' && !hasWebSessionCookie()) {
      toast.warning(
        'Tu sesión no se va a mantener activa en este navegador al cerrar o actualizar la página. Revisá que las cookies estén habilitadas si querés evitarlo.',
        'Sesión no persistente'
      );
    }
  };

  // El correo puede resolver a más de una cuenta — userRepo.login lanza
  // TenantSelectionRequiredError en ese caso; el caller (Login.tsx) debe
  // capturarlo y mostrar el selector de tenant en vez de continuar la sesión acá.
  const login = async (email: string, pin: string, tenantId?: string): Promise<void> => {
    const user = await userRepo.login(email, pin, tenantId);

    setCurrentUser(user);
    safeStorage.setItem('hommi_user', JSON.stringify(user));
    // El login guarda el tenant en el apiClient (para el header x-tenant-id);
    // sin subirlo al estado, la UI no sabe en qué residencial está la sesión y
    // el badge del sidebar cae al nombre genérico de settings.
    hydrateCurrentTenant();
    warnIfSessionWontPersist();

    // /auth/login puede devolver un user sin todos los detalles (ej. isPrincipal
    // o units incompletos frente a /users/me) — refrescamos de una vez para que
    // el rol/permisos sean correctos desde el primer momento de la sesión, sin
    // esperar al próximo reload de página.
    try {
      await refreshCurrentUser();
    } catch {
      // El login ya fue exitoso con `user`; si este refresh falla, seguimos con eso.
    }

    // Eagerly cache settings so SettingsContext can apply branding immediately
    try {
      const settings = await settingsRepo.getSettings();
      writeSettingsCache(settings);
    } catch {
      // Non-fatal — SettingsContext will retry via its own effect
    }
  };

  const logout = (): void => {
    if (API_MODE === 'api') {
      userRepo.logout().catch(console.error);
    }
    setCurrentUser(null);
    setCurrentTenant(null);
    safeStorage.removeItem('hommi_user');
    safeStorage.removeItem('hommi_tenant');
    clearCurrentTenant();
    clearTokens();
    // Sin esto los tokens del admin original sobrevivían al logout en la cookie (7 días).
    removeCookie(IMPERSONATION_STORAGE_KEY);
    setImpersonation(null);
  };

  // Cambiar de residencial es re-loguearse contra la cuenta de ese tenant: el
  // alta crea un MasterUser por tenant, con su propia contraseña. Tras el login
  // se recarga la app entera en vez de intentar invalidar a mano settings,
  // abilities y todas las listas cacheadas del tenant anterior.
  const switchTenant = async (tenant: TenantSelection, password: string): Promise<void> => {
    await login(currentUser?.email ?? '', password, tenant.tenantId);
    window.location.assign('/');
  };

  const loginWithMagicLink = async (response: MagicLinkVerifyResponse): Promise<void> => {
    setCurrentUser(response.user);
    safeStorage.setItem('hommi_user', JSON.stringify(response.user));
    if (response.tenant) {
      setCurrentTenant(response.tenant);
      safeStorage.setItem('hommi_tenant', JSON.stringify(response.tenant));
    }
    warnIfSessionWontPersist();

    // Mismo caso que login(): el user del magic-link puede venir sin todos los
    // detalles (isPrincipal, units) frente a /users/me.
    try {
      await refreshCurrentUser();
    } catch {
      // La sesión ya quedó activa con response.user; si el refresh falla, seguimos con eso.
    }

    try {
      const settings = await settingsRepo.getSettings();
      writeSettingsCache(settings);
    } catch {
      // Non-fatal
    }
  };

  const selectTenantForMagicLink = async (
    userId: string,
    tenantId: string
  ): Promise<MagicLinkVerifyResponse> => {
    const response = await userRepo.selectTenantForMagicLink(userId, tenantId);
    await loginWithMagicLink(response);
    return response;
  };

  const updateCurrentUser = async (userData: Partial<User>): Promise<void> => {
    if (!currentUser) throw new Error('No user logged in');
    const updated = await userRepo.updateProfile(userData);
    setCurrentUser({ ...currentUser, ...updated });
  };

  const refreshCurrentUser = async (): Promise<void> => {
    const user = await userRepo.getCurrentUser();
    setCurrentUser(user);
    safeStorage.setItem('hommi_user', JSON.stringify(user));
  };

  const startImpersonation = async (memberId: string): Promise<void> => {
    if (!currentUser) throw new Error('No user logged in');

    const originalAccessToken = getAccessToken();
    if (!originalAccessToken) throw new Error('No active session to impersonate from');
    const originalRefreshToken = getRefreshToken();

    const response = await userRepo.impersonateMember(memberId);
    const memberUser = await userRepo.getUserById(memberId);
    if (!memberUser) throw new Error('Impersonated user not found');

    const snapshot: ImpersonationSnapshot = {
      originalAdmin: response.originalAdmin,
      impersonating: response.impersonating,
      originalAccessToken,
      originalRefreshToken,
      originalUser: currentUser,
    };

    await setTokens(response.accessToken, response.refreshToken);
    setCurrentUser(memberUser);
    safeStorage.setItem('hommi_user', JSON.stringify(memberUser));
    setCookie(IMPERSONATION_STORAGE_KEY, JSON.stringify(snapshot), IMPERSONATION_COOKIE_MAX_AGE_SECONDS);
    setImpersonation({ originalAdmin: response.originalAdmin, impersonating: response.impersonating });
  };

  const exitImpersonation = async (): Promise<void> => {
    const stored = getCookie(IMPERSONATION_STORAGE_KEY);
    if (!stored) {
      setImpersonation(null);
      return;
    }

    const snapshot: ImpersonationSnapshot = JSON.parse(stored);
    await setTokens(snapshot.originalAccessToken, snapshot.originalRefreshToken ?? undefined);
    setCurrentUser(snapshot.originalUser);
    safeStorage.setItem('hommi_user', JSON.stringify(snapshot.originalUser));
    removeCookie(IMPERSONATION_STORAGE_KEY);
    setImpersonation(null);
  };

  return (
    <AuthContext.Provider
      value={{
        currentUser,
        setCurrentUser,
        isLoadingUser,
        currentTenant,
        switchTenant,
        login,
        logout,
        loginWithMagicLink,
        selectTenantForMagicLink,
        updateCurrentUser,
        refreshCurrentUser,
        impersonation,
        startImpersonation,
        exitImpersonation,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
};
